import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";

/**
 * AI テキスト生成（変換 / 校正）API。
 *
 * ポジション × シーンに応じてテキストを生成/校正する単一エンドポイント。
 * プロバイダ・モデル・有効/無効・プロンプト/ルールは BE 側（管理者）が
 * Django admin で変更できる。FE は識別子（scene / project_id / shot_id 等）
 * のみを送り、コメント履歴などの文脈は BE が組み立てる。
 *
 * シーン一覧（BE の app/app_services/ai/scenes.py と対応）:
 *   - `chikuchiku_filter`            : ショットコメント（クライアント）の校正
 *   - `chikuchiku_filter_dm`         : DM 送信者のメッセージ校正
 *   - `chikuchiku_filter_chat`       : Chat（オファー非依存 DM）送信者の校正
 *   - `chikuchiku_filter_talkroom`   : トークルーム（クライアント）の校正
 *   - `reply_assistant_shot`         : ショットコメント（プロデューサー/ディレクター）の返信生成
 *   - `reply_assistant_dm`           : DM 受託者の返信生成
 *   - `reply_assistant_chat`         : Chat（オファー非依存 DM）の返信生成
 *   - `reply_assistant_talkroom`     : トークルーム（プロデューサー）の返信生成
 *   - `review_assistant_offer`       : オファー完了後の相互レビュー生成
 *
 * 校正系（chikuchiku_*）は入力テキスト必須、返信生成系（reply_assistant_* /
 * review_assistant_offer）は下書き無し（空テキスト）でも実行できる。
 */

// 利用可能なシーンキー（BE の app/app_services/ai/scenes.py と対応）
export type AITextScene =
  | "chikuchiku_filter"
  | "chikuchiku_filter_dm"
  | "chikuchiku_filter_chat"
  | "chikuchiku_filter_talkroom"
  | "reply_assistant_shot"
  | "reply_assistant_dm"
  | "reply_assistant_chat"
  | "reply_assistant_talkroom"
  | "review_assistant_offer";

export interface TransformTextParams {
  /** シーンキー（例: "chikuchiku_filter"） */
  scene: AITextScene;
  /** 校正/変換対象のテキスト（返信生成系は空可） */
  text: string;
  /** 権限チェック + トークルーム履歴のソースとなるプロジェクトID */
  project_id: string;
  /** コメント履歴を文脈に含めるためのショットID（任意） */
  shot_id?: string | null;
  /** メッセージ履歴を文脈に含めるための DM スレッドID（任意） */
  dm_id?: string | null;
  /** メッセージ履歴を文脈に含めるための Chat（オファー非依存 DM）スレッドID（任意） */
  chat_id?: string | null;
  /** 編集時、自分自身のコメント/メッセージを履歴から除外するための ID（任意） */
  exclude_comment_id?: string | null;
  /** レビュー生成シーンで重視するテーマ（例: "創造性"）。任意。 */
  theme?: string | null;
  /** レビュー生成シーンの全体評価（"LIKE" | "DISLIKE"）。任意。 */
  rating?: string | null;
}

export interface TransformTextData {
  /** 校正/変換後のテキスト */
  transformed_text: string;
  /** 適用されたシーン */
  scene: string;
  /** 使用された AI プロバイダ */
  provider: string;
  /** 使用されたモデル名 */
  model: string;
}

export interface TransformTextResponse {
  status: string;
  data: TransformTextData;
}

export const aiTextApi = createApi({
  reducerPath: "aiTextApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  endpoints: (builder) => ({
    transformText: builder.mutation<TransformTextResponse, TransformTextParams>({
      query: ({ scene, text, project_id, shot_id, dm_id, chat_id, exclude_comment_id, theme, rating }) => ({
        url: `/api/ai/text-transform`,
        method: "POST",
        body: {
          scene,
          text,
          project_id,
          ...(shot_id ? { shot_id } : {}),
          ...(dm_id ? { dm_id } : {}),
          ...(chat_id ? { chat_id } : {}),
          ...(exclude_comment_id ? { exclude_comment_id } : {}),
          ...(theme ? { theme } : {}),
          ...(rating ? { rating } : {}),
        },
      }),
    }),
  }),
});

export const { useTransformTextMutation } = aiTextApi;
